/**
 * Guarda Mundo - Instagram mobile carousel: âncora esquerda (2 fotos + peek), última à direita.
 */
import { scrollIntoView } from '@theme/scrolling';

if (!customElements.get('gm-instagram-carousel')) {
  class GmInstagramCarousel extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector('.gm-mobile-instagram__viewport');
      this._slides = [...this.querySelectorAll('.gm-mobile-instagram__slide')];
      this._prevBtn = this.querySelector('[data-gm-insta-prev]');
      this._nextBtn = this.querySelector('[data-gm-insta-next]');

      if (!this._viewport || this._slides.length < 2) return;

      this._activeIndex = 0;
      this._onScroll = () => {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(() => this._syncFromScroll());
      };

      this._viewport.addEventListener('scroll', this._onScroll, { passive: true });

      this._prevBtn?.addEventListener('click', () => this._goTo(this._activeIndex - 1));
      this._nextBtn?.addEventListener('click', () => this._goTo(this._activeIndex + 1));

      this._resizeObserver = new ResizeObserver(() => this._syncFromScroll());
      this._resizeObserver.observe(this._viewport);

      this._syncFromScroll();
    }

    disconnectedCallback() {
      this._viewport?.removeEventListener('scroll', this._onScroll);
      this._resizeObserver?.disconnect();
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    }

    _getLastIndex() {
      return this._slides.length - 1;
    }

    /** ScrollLeft de cada âncora: início do slide, exceto o último (fim alinhado à direita). */
    _getSnapScrollLeft(index) {
      const slide = this._slides[index];
      if (!slide) return 0;

      if (index === this._getLastIndex()) {
        return slide.offsetLeft + slide.offsetWidth - this._viewport.clientWidth;
      }

      return slide.offsetLeft;
    }

    _syncFromScroll() {
      const lastIndex = this._getLastIndex();
      const scrollLeft = this._viewport.scrollLeft;
      let bestIndex = 0;
      let bestDistance = Infinity;

      for (let index = 0; index <= lastIndex; index += 1) {
        const distance = Math.abs(scrollLeft - this._getSnapScrollLeft(index));
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      }

      this._setActive(bestIndex);
    }

    _setActive(index) {
      const lastIndex = this._getLastIndex();
      index = Math.max(0, Math.min(index, lastIndex));

      if (index === this._activeIndex && this._prevBtn?.disabled === (index === 0)) return;

      this._activeIndex = index;

      if (this._prevBtn) {
        this._prevBtn.disabled = index === 0;
      }

      if (this._nextBtn) {
        this._nextBtn.disabled = index === lastIndex;
      }
    }

    _goTo(index) {
      const slide = this._slides[index];
      if (!slide) return;

      const lastIndex = this._getLastIndex();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      scrollIntoView(slide, {
        ancestor: this._viewport,
        inline: index === lastIndex ? 'end' : 'start',
        block: 'nearest',
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
      this._setActive(index);
    }
  }

  customElements.define('gm-instagram-carousel', GmInstagramCarousel);
}
